// The warehouse clock.
//
// Fernando (2026-09-08): order times were often wrong or missing and some
// showed UTC. Rule: the database stores INSTANTS as naive UTC (every *_at
// column, and since migration 024 also orders.order_date); the screen shows
// them in the WAREHOUSE timezone — the Settings → General value, Europe/Tallinn
// today — never the browser's zone and never the shop's.
//
// fmtWh() is the one formatter for those instants: a string with an offset or
// a trailing Z is an instant as it stands; a naive string is UTC and gets its
// Z. Do NOT pass it the statistics module's values: that SQL already returns
// wall-clock strings via wh_local(), and statistics/shared.tsx formats them as
// they are.
import { authFetch, API } from './api';

const KEY = 'wms_tz';
const DEFAULT_TZ = 'Europe/Tallinn';

export function whTz(): string {
  try { return localStorage.getItem(KEY) || DEFAULT_TZ; } catch { return DEFAULT_TZ; }
}

/** Refresh the cached warehouse timezone from Settings. Call once per signed-in session. */
export async function loadWarehouseTz(): Promise<string> {
  try {
    const r = await authFetch(`${API}/settings/timezone`);
    if (r.ok) {
      const d = await r.json();
      if (d?.current_tz) { localStorage.setItem(KEY, d.current_tz); return d.current_tz; }
    }
  } catch { /* keep the cached value */ }
  return whTz();
}

function asInstant(ts: string | Date | null | undefined): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  const s = String(ts).trim();
  if (!s) return null;
  // offset or Z present → an instant already; otherwise naive UTC from the DB
  const hasZone = /(?:Z|[+-]\d\d:?\d\d)$/.test(s);
  const d = new Date(hasZone ? s : s.replace(' ', 'T') + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

export type WhStyle = 'datetime' | 'seconds' | 'short' | 'time' | 'date';

const OPTS: Record<WhStyle, Intl.DateTimeFormatOptions> = {
  datetime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  seconds:  { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' },
  short:    { dateStyle: 'short', timeStyle: 'short' },
  time:     { hour: '2-digit', minute: '2-digit' },
  date:     { day: '2-digit', month: '2-digit', year: 'numeric' },
};

/** An instant from the WMS, formatted on the warehouse clock. '' when empty/unparseable. */
export function fmtWh(ts: string | Date | null | undefined, style: WhStyle = 'datetime'): string {
  const d = asInstant(ts);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat('et-EE', { ...OPTS[style], timeZone: whTz() }).format(d);
  } catch {
    return new Intl.DateTimeFormat('et-EE', OPTS[style]).format(d);
  }
}
